import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { List } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { api } from "../lib/api";
import SEOHead from "../components/SEOHead";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import { Button } from "../components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import NotFound from "./NotFound";

export default function SEOLandingPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [tocItems, setTocItems] = useState([]);
  const { field, t, language } = useLanguage();
  const contentRef = useRef(null);

  useEffect(() => {
    setNotFound(false); setPage(null); setTocItems([]);
    api.get(`/seo-pages/${slug}`).then(({ data }) => setPage(data)).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!page || !contentRef.current) return;
    const headings = Array.from(contentRef.current.querySelectorAll("h2[id]"));
    setTocItems(headings.map((h) => ({ id: h.id, label: h.textContent })));
  }, [page, language]);

  if (notFound) return <NotFound />;
  if (!page) return <div className="min-h-[50vh]" />;

  const faqItems = page.faq_items || [];
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const schemas = [
    buildBreadcrumbSchema([{ label: field(page, "h1") }]),
    { "@context": "https://schema.org", "@type": "Article", headline: field(page, "h1"), description: field(page, "meta_description"), url: `${origin}/${slug}`, publisher: { "@type": "Organization", name: "Buy Likes and Followers" } },
  ];
  if (faqItems.length > 0) {
    schemas.push({
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: faqItems.map((f) => ({
        "@type": "Question", name: language === "en" ? f.q_en : f.q_fr,
        acceptedAnswer: { "@type": "Answer", text: language === "en" ? f.a_en : f.a_fr },
      })),
    });
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-20" data-testid="seo-landing-page">
      <SEOHead title={field(page, "title")} description={field(page, "meta_description")} ogType="article" schema={schemas} />
      <Breadcrumbs items={[{ label: field(page, "h1") }]} />
      <h1 className="font-heading text-4xl font-bold text-white mb-6" data-testid="seo-page-h1">{field(page, "h1")}</h1>

      {tocItems.length >= 2 && (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5 mb-8" data-testid="seo-page-toc">
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-zinc-400 mb-3">
            <List className="w-3.5 h-3.5" /> {t("seo.toc_title", "Sommaire")}
          </p>
          <ol className="space-y-1.5">
            {tocItems.map((item, i) => (
              <li key={item.id}>
                <a href={`#${item.id}`} className="text-sm text-indigo-400 hover:text-indigo-300 hover:underline" data-testid={`seo-page-toc-link-${i}`}>
                  {i + 1}. {item.label}
                </a>
              </li>
            ))}
          </ol>
        </div>
      )}

      <div ref={contentRef} className="seo-content mb-10" dangerouslySetInnerHTML={{ __html: field(page, "content") }} />

      {faqItems.length > 0 && (
        <div className="mb-10" data-testid="seo-page-faq">
          <h2 className="font-heading text-2xl font-bold text-white mb-4">{t("seo.faq_title", "Questions fréquentes")}</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-white/10" data-testid={`seo-page-faq-item-${i}`}>
                <AccordionTrigger className="text-white text-left hover:no-underline">{language === "en" ? f.q_en : f.q_fr}</AccordionTrigger>
                <AccordionContent className="text-zinc-400">{language === "en" ? f.a_en : f.a_fr}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}

      {page.related_network && (
        <Link to={`/reseaux/${page.related_network}`}>
          <Button className="rounded-full bg-indigo-500 hover:bg-indigo-600" data-testid="seo-page-cta">{t("common.view_services", "Voir les services")}</Button>
        </Link>
      )}
    </div>
  );
}
